 
import translate from "@insite/client-framework/Translate";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
// import Link from "@insite/mobius/Link"; // Custom Code
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { css } from "styled-components";

/* Custom Code */
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import useExpandProps from "@store/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import Heading, { HeadingPresentationProps } from "@store/Library/Components/Toolkit/Heading";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";

export interface CardListHeadingStyles {
    heading?: HeadingPresentationProps;
    link?: LinkPresentationProps;
}

export const cardListHeadingStyles: CardListHeadingStyles = {
    heading: {
        variant: "h4",
        forwardAs: "h2",
        itemOffset: 0.3,
        extendedStyles: {
            title: {
                css: css`
                    ${spacingStyles(null, "0.75em")}
                    padding-bottom: 0.6em;
                    border-bottom: 1px solid ${getColor("common.border")};
                `,
            },
        },
    },
    link: linkGetActionLinkProps(),
};
/* Custom Code */

interface OwnProps {
    heading: string;
    viewAllUrl?: string;
    // Custom Code
    hasNoResults?: boolean;
    extendedStyles?: CardListHeadingStyles;
    overridedStyles?: CardListHeadingStyles;
    children?: React.ReactNode;
}

const CardListHeading: React.FunctionComponent<OwnProps> = ({
    heading,
    viewAllUrl,
    // Custom Code
    hasNoResults,
    extendedStyles,
    overridedStyles,
    children,
}) => {
    /* Custom Code */
    const styles = useExpandProps(cardListHeadingStyles, extendedStyles, overridedStyles);

    const headingStyles = React.useMemo(() => {
        if (hasNoResults) {
            return styles.heading;
        }

        return mergeProps<CardListHeadingStyles["heading"]>(styles.heading, {
            extendedStyles: {
                title: {
                    css: css`
                        margin: 0;
                        border-bottom: 0;
                    `,
                },
            },
        });
    }, [styles.heading, hasNoResults]);

    return (
        <Heading title={heading} {...headingStyles} className="card-list-heading">
            {children}
            {viewAllUrl && (
                <Link
                    href={viewAllUrl}
                    icon={{ iconProps: { src: ChevronRight } }}
                    {...styles.link}
                    className="view-all-link"
                    data-test-selector="cardListHeadingViewAllLink"
                >
                    {translate("View All")}
                    <VisuallyHidden>
                        {translate("View All")} {heading}
                    </VisuallyHidden>
                </Link>
            )}
        </Heading>
    );
    /* Custom Code */
};

export default CardListHeading;
